/**
 * Data Saver Utility
 * Sends data directly to backend at submit time. No cache for persistence.
 */

import { SALON_DETECTION_SESSION_KEY } from '../config/salonApp';
import { getApiBaseUrl, SALON_API_PREFIX } from './apiConfig';
import { fetchWithTimeout } from './fetchWithTimeout';
import { getFranchiseStylistId } from '../../shared/franchiseFlowAuth';

const API_BASE_URL = (getApiBaseUrl() || '').replace(/\/$/, '');

/** Build contact + user payload (snake_case). Omit empty optional fields so saves don't wipe DB values. */
function toBackendUserContact(userOrContact = {}) {
  const out = {};
  const name = userOrContact.name ?? '';
  const mobile = userOrContact.mobile_number ?? userOrContact.mobileNumber ?? '';
  const email = userOrContact.email;
  const instagram = userOrContact.instagram_id ?? userOrContact.instagramId;
  const gender = userOrContact.gender;
  const skinType = userOrContact.skin_type ?? userOrContact.skinType;
  const age = userOrContact.age;
  const faceUserId = userOrContact.face_user_id ?? userOrContact.faceUserId;
  const dob =
    userOrContact.date_of_birth ?? userOrContact.dateOfBirth ?? userOrContact.dob;

  if (name != null && String(name).trim() !== '') out.name = String(name).trim();
  if (mobile != null && String(mobile).trim() !== '') out.mobile_number = String(mobile).trim();
  if (email != null && String(email).trim() !== '') out.email = String(email).trim();
  if (instagram != null && String(instagram).trim() !== '') {
    out.instagram_id = String(instagram).trim();
  }
  if (gender != null && String(gender).trim() !== '') out.gender = String(gender).trim();
  if (skinType != null && String(skinType).trim() !== '') out.skin_type = String(skinType).trim();
  if (age != null && String(age).trim() !== '') {
    const ageNum = parseInt(age, 10);
    out.age = Number.isNaN(ageNum) ? age : ageNum;
  }
  if (faceUserId != null && String(faceUserId).trim() !== '') {
    out.face_user_id = String(faceUserId).trim();
  }
  if (dob != null && String(dob).trim() !== '') out.date_of_birth = String(dob).trim();
  return out;
}

const SESSION_STORAGE_KEY = SALON_DETECTION_SESSION_KEY;
const PENDING_FLUSH_DELAY_MS = 5000;

let _flushTimer = null;

let _pendingBySession = {};

function _loadPending() {
  return _pendingBySession;
}

function _persistPending() {
  /* in-memory only */
}

function _mergeIntoPending(sessionId, patch) {
  if (sessionId == null || String(sessionId).trim() === '') return;
  const sid = String(sessionId).trim();
  const pending = _loadPending();
  const prev = (pending[sid] && typeof pending[sid] === 'object') ? pending[sid] : {};
  pending[sid] = { ...prev, ...patch };
  _persistPending();
}

function _clearPending(sessionId) {
  const sid = sessionId == null ? '' : String(sessionId).trim();
  if (!sid) return;
  const pending = _loadPending();
  if (pending[sid]) {
    delete pending[sid];
    _persistPending();
  }
}

function _scheduleSafetyFlush(sessionId, appSessionToken = null) {
  if (_flushTimer) return;
  _flushTimer = setTimeout(() => {
    _flushTimer = null;
    flushPendingSaveAnalysis(sessionId, appSessionToken).catch(() => {});
  }, PENDING_FLUSH_DELAY_MS);
}

async function saveToBackend(saveData, appSessionToken = null) {
  const payload = { ...saveData };
  // Keep identifiers canonical: if session_id exists, force skin_id to the same value.
  // This prevents stale IDs from overridden/extra payload fields.
  if (payload.session_id != null && String(payload.session_id).trim() !== '') {
    const sid = String(payload.session_id).trim();
    payload.session_id = sid;
    payload.skin_id = sid;
  }
  if (appSessionToken && String(appSessionToken).trim()) {
    payload.app_session_token = String(appSessionToken).trim();
  }
  const response = await fetchWithTimeout(`${API_BASE_URL}${SALON_API_PREFIX}/save-analysis`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    timeoutMs: 45000,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to save data');
  }
  const data = await response.json();
  if (data?.persistence_verified === false) {
    const error = new Error('Analysis saved but persistence verification failed');
    error.persistenceVerified = false;
    throw error;
  }
  // Adopt skin_id from response only when it matches what we sent, to prevent an unexpected
  // server response from silently switching the active session to a stale reference.
  if (data && data.skin_id != null) {
    const skinIdStr = String(data.skin_id);
    const sentSid = (payload.session_id != null ? String(payload.session_id) : '').trim();
    const okToAdopt = !sentSid || skinIdStr === sentSid;
    if (okToAdopt && localStorage.getItem(SESSION_STORAGE_KEY) !== skinIdStr) {
      localStorage.setItem(SESSION_STORAGE_KEY, skinIdStr);
    }
  }
  return data;
}

/** Extract only scores from skin conditions for payload size */
function extractScoresOnly(skinConditions) {
  if (!skinConditions || typeof skinConditions !== 'object') return null;
  const keys = ['hydration', 'oxygen', 'smoothness', 'dullness', 'uneven_skintone'];
  const out = {};
  keys.forEach(k => {
    if (skinConditions[k] && typeof skinConditions[k] === 'object')
      out[k] = { score: skinConditions[k].score ?? null };
  });
  return Object.keys(out).length ? out : null;
}

/** Build payload identifier - session_id and skin_id (backend resolves both) */
function toIdentifier(sessionId) {
  if (!sessionId) return {};
  const s = String(sessionId).trim();
  return { session_id: s, skin_id: s };
}

/** Record a flow-stage flag in salon_users_flow_minimal only. */
export async function recordSalonFlowStage(sessionId, stage, appSessionToken = null, metadata = null) {
  const eventData = { stage: String(stage) };
  if (metadata && typeof metadata === 'object' && Object.keys(metadata).length > 0) {
    eventData.event_metadata = metadata;
  }
  return saveSalonAnalyticsEvent(sessionId, eventData, appSessionToken);
}

export async function saveSalonAnalyticsEvent(sessionId, eventData = {}, appSessionToken = null) {
  if (sessionId == null || String(sessionId).trim() === '') return { success: false, error: 'Session/skin ID is required' };
  try {
    const sid = String(sessionId).trim();
    const stage = eventData?.event_type || eventData?.step_name || eventData?.stage || 'event';
    const payload = { ...eventData };
    delete payload.event_type;
    delete payload.step_name;
    delete payload.stage;

    const body = {
      session_id: sid,
      skin_id: sid,
      salon_reference: sid,
      stage: String(stage),
      payload: Object.keys(payload).length ? payload : null,
      event_time: new Date().toISOString(),
    };
    if (appSessionToken && String(appSessionToken).trim()) {
      body.app_session_token = String(appSessionToken).trim();
    }
    const stylistRaw = getFranchiseStylistId();
    if (stylistRaw != null && String(stylistRaw).trim() !== '') {
      const parsed = parseInt(String(stylistRaw).trim(), 10);
      body.stylist_id = Number.isNaN(parsed) ? stylistRaw : parsed;
    }

    const resp = await fetchWithTimeout(`${API_BASE_URL}${SALON_API_PREFIX}/record-flow-stage`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      timeoutMs: 6000,
    }).catch(() => null);
    if (!resp) return { success: false, skipped: true };
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      return { success: false, error: err.error || 'Failed to record event' };
    }
    const d = await resp.json().catch(() => ({}));
    return { success: true, event_id: d?.event_id };
  } catch (e) {
    return { success: false, error: e?.message || 'internal' };
  }
}

export async function flushPendingSaveAnalysis(sessionId, appSessionToken = null) {
  if (sessionId == null || String(sessionId).trim() === '') return { success: false, error: 'Session/skin ID is required' };
  const sid = String(sessionId).trim();
  const pending = _loadPending();
  const staged = pending[sid];
  if (!staged || typeof staged !== 'object' || Object.keys(staged).length === 0) return { success: true, skipped: true };
  try {
    await saveToBackend({ ...toIdentifier(sid), ...staged }, appSessionToken);
    _clearPending(sid);
    return { success: true, persistenceVerified: true };
  } catch (e) {
    return { success: false, error: e.message, persistenceVerified: e.persistenceVerified === false ? false : undefined };
  }
}

/**
 * Save only device_info for an existing session (e.g. right after creating session on landing CTA).
 * @param {string} sessionId - session_id / skin_id from backend
 * @param {object} deviceInfo - device info from captureDeviceInfo()
 */
export async function saveDeviceInfo(sessionId, deviceInfo, appSessionToken = null) {
  if (!sessionId || String(sessionId).trim() === '') return { success: false, error: 'Session ID required' };
  if (!deviceInfo || typeof deviceInfo !== 'object' || Object.keys(deviceInfo).length === 0) return { success: true };
  try {
    await saveToBackend({ ...toIdentifier(sessionId), device_info: deviceInfo }, appSessionToken);
    return { success: true };
  } catch (e) {
    return { success: false, error: e.message };
  }
}

/**
 * Save user info (gender, skin_type, age) directly to backend. No cache. Does not send contact fields.
 * @param {string|number} sessionId - session_id or skin_id (numeric string)
 * @param {object} userData - { gender, skinType, age, ... }
 * @param {object} [deviceInfo] - optional device info captured at landing CTA (user_agent, platform, device_type, etc.)
 */
export async function saveUserInfo(sessionId, userData, deviceInfo = null, appSessionToken = null) {
  if (sessionId == null || String(sessionId).trim() === '')
    return { success: false, error: 'Session/skin ID is required' };
  try {
    const payload = {
      ...toIdentifier(sessionId),
      gender: userData.gender ?? null,
      skin_type: userData.skin_type ?? userData.skinType ?? null,
      age: userData.age ?? null
    };
    if (deviceInfo && typeof deviceInfo === 'object' && Object.keys(deviceInfo).length > 0) {
      payload.device_info = deviceInfo;
    }
    await saveToBackend(payload, appSessionToken);
    return { success: true };
  } catch (e) {
    return { success: false, error: e.message };
  }
}

/**
 * Save contact + demographics in one request (salon customer info page).
 * @param {object} [extra] - optional questionnaire answers merged into payload (label → value)
 */
export async function saveSalonUserProfile(sessionId, profile, deviceInfo = null, appSessionToken = null, extra = null) {
  if (sessionId == null || String(sessionId).trim() === '')
    return { success: false, error: 'Session/skin ID is required' };
  try {
    const { name, mobileNumber, email, gender, skinType, age } = profile || {};
    const payload = {
      ...toIdentifier(sessionId),
      ...toBackendUserContact({ name, mobileNumber, email, gender, skinType, age }),
    };
    if (extra && typeof extra === 'object') {
      // Prevent dynamic form answers from overwriting active session identifiers.
      const sanitizedExtra = { ...extra };
      delete sanitizedExtra.session_id;
      delete sanitizedExtra.skin_id;
      delete sanitizedExtra.salon_reference;
      delete sanitizedExtra.previous_salon_reference;
      delete sanitizedExtra.prior_salon_reference;
      Object.assign(payload, sanitizedExtra);
    }
    if (deviceInfo && typeof deviceInfo === 'object' && Object.keys(deviceInfo).length > 0) {
      payload.device_info = deviceInfo;
    }
    await saveToBackend(payload, appSessionToken);
    saveSalonAnalyticsEvent(
      sessionId,
      {
        event_type: 'user_info_saved',
        event_category: 'user',
        step_name: 'user_info',
        page_name: 'UserInfoPage',
      },
      appSessionToken,
    ).catch(() => {});
    return { success: true };
  } catch (e) {
    return { success: false, error: e.message };
  }
}

/**
 * Save contact + user info directly to backend. Optionally include AQI. No cache.
 * @param {string} sessionId
 * @param {object} contactData - { name, mobileNumber, email, instagramId }
 * @param {object} userInfo - { gender, skinType, age }
 * @param {object} aqiData - optional { success, uv_index, temperature, humidity, wind_speed, location }
 */
export async function saveContactInfo(sessionId, contactData, userInfo = {}, aqiData = null, appSessionToken = null) {
  if (sessionId == null || String(sessionId).trim() === '')
    return { success: false, error: 'Session/skin ID is required' };
  try {
    const payload = {
      ...toBackendUserContact({ ...userInfo, ...contactData })
    };
    if (aqiData && aqiData.success) {
      payload.uv_index = aqiData.uv_index ?? null;
      payload.temperature = aqiData.temperature ?? null;
      payload.humidity = aqiData.humidity ?? null;
      payload.wind_speed = aqiData.wind_speed ?? null;
      payload.location = aqiData.location ?? null;
      payload.latitude = aqiData.latitude ?? null;
      payload.longitude = aqiData.longitude ?? null;
    }
    // Stage (merge) and let a later flush coalesce this with other updates (AQI, detection, goals).
    _mergeIntoPending(sessionId, payload);
    _scheduleSafetyFlush(sessionId, appSessionToken);
    return { success: true };
  } catch (e) {
    return { success: false, error: e.message };
  }
}

/**
 * Save skin detection results + optional user/contact. No cache.
 * @param {string} sessionId
 * @param {object} skinResults - { detections, skinScore, skinConditions, markedImage }
 * @param {object} userAndContact - optional { name, mobile_number, email, instagram_id, gender, skin_type, age }
 */
export async function saveSkinDetection(sessionId, skinResults, userAndContact = {}, appSessionToken = null) {
  if (sessionId == null || String(sessionId).trim() === '')
    return { success: false, error: 'Session/skin ID is required' };
  try {
    const scoresOnly = extractScoresOnly(skinResults.skinConditions);
    const sideResultsSummary = Array.isArray(skinResults.sideResults)
      ? skinResults.sideResults
        .map((item) => {
          if (!item || typeof item !== 'object') return null;
          const key = item.key || item.view;
          if (!key) return null;
          return {
            key,
            detections: Array.isArray(item.detections) ? item.detections : [],
            roi_region_landmarks_px:
              item.roi_region_landmarks_px && typeof item.roi_region_landmarks_px === 'object'
                ? item.roi_region_landmarks_px
                : undefined,
          };
        })
        .filter(Boolean)
      : [];

    const payload = {
      skin_data: {
        detections: skinResults.detections || [],
        front_detections: skinResults.frontDetections || skinResults.detections || [],
        side_results: sideResultsSummary,
        skinScore: skinResults.skinScore ?? null,
        skinConditions: scoresOnly
      },
      ...toBackendUserContact(userAndContact)
    };
    // Flush a single combined request: staged (contact/aqi/etc) + detection payload.
    _mergeIntoPending(sessionId, payload);
    const flushResult = await flushPendingSaveAnalysis(sessionId, appSessionToken);
    if (!flushResult?.success && !flushResult?.skipped) {
      _scheduleSafetyFlush(sessionId, appSessionToken);
      return { success: false, error: flushResult?.error || 'Failed to save analysis', retryable: true };
    }
    if (flushResult?.persistenceVerified === false) {
      return { success: false, error: 'Analysis persistence could not be verified', retryable: true };
    }
    saveSalonAnalyticsEvent(
      sessionId,
      {
        event_type: 'analysis_completed',
        event_category: 'analysis',
        step_name: 'skin_detection',
        page_name: 'AISkinAnalysisPage',
      },
      appSessionToken,
    ).catch(() => {});
    return { success: true, persistenceVerified: flushResult?.persistenceVerified !== false };
  } catch (e) {
    return { success: false, error: e.message };
  }
}

/**
 * Save skin goals + optional AQI + user/contact/skinData from state. No cache.
 * @param {string} sessionId
 * @param {object} skinGoalData
 * @param {object} aqiData - optional
 * @param {object} options - { userData, skinResults } from location.state
 */
export async function saveSkinGoals(sessionId, skinGoalData, aqiData = null, options = {}, appSessionToken = null) {
  if (sessionId == null || String(sessionId).trim() === '')
    return { success: false, error: 'Session/skin ID is required' };
  try {
    const { userData = {}, skinResults = null } = options;
    let skinDataToSave = null;
    if (skinResults && (skinResults.detections || skinResults.skinConditions)) {
      skinDataToSave = {
        detections: skinResults.detections || [],
        skinScore: skinResults.skinScore ?? null,
        skinConditions: extractScoresOnly(skinResults.skinConditions)
      };
    }
    const payload = {
      ...toBackendUserContact(userData),
      skin_data: skinDataToSave,
      skin_goal: skinGoalData
    };
    if (aqiData && aqiData.success) {
      payload.uv_index = aqiData.uv_index ?? null;
      payload.temperature = aqiData.temperature ?? null;
      payload.humidity = aqiData.humidity ?? null;
      payload.wind_speed = aqiData.wind_speed ?? null;
      payload.location = aqiData.location ?? null;
      payload.latitude = aqiData.latitude ?? null;
      payload.longitude = aqiData.longitude ?? null;
    }
    _mergeIntoPending(sessionId, payload);
    flushPendingSaveAnalysis(sessionId, appSessionToken).catch(() => {});
    return { success: true };
  } catch (e) {
    return { success: false, error: e.message };
  }
}

/** Save only AQI/weather update for an existing session. No cache. */
export async function saveAqiUpdate(sessionId, aqiData, appSessionToken = null) {
  if (sessionId == null || !aqiData || !aqiData.success) return { success: false };
  try {
    const payload = {
      uv_index: aqiData.uv_index ?? null,
      temperature: aqiData.temperature ?? null,
      humidity: aqiData.humidity ?? null,
      wind_speed: aqiData.wind_speed ?? null,
      location: aqiData.location ?? null,
      latitude: aqiData.latitude ?? null,
      longitude: aqiData.longitude ?? null
    };
    _mergeIntoPending(sessionId, payload);
    _scheduleSafetyFlush(sessionId, appSessionToken);
    return { success: true };
  } catch (e) {
    return { success: false, error: e.message };
  }
}
